import { Link, useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Seo, SITE_URL } from "../components/Seo";
import { SEO_PAGES } from "../data/seoPages";
import { LeadCaptureForm } from "../components/LeadCaptureForm";
import { Button } from "../components/ui/button";
import { Plane, Check, ArrowRight, GraduationCap, Star } from "lucide-react";

export default function SeoLanding() {
  const { slug } = useParams();
  const page = SEO_PAGES[slug];
  if (!page) return <Navigate to="/" replace />;

  const path = `/learn/${slug}`;
  const courseSchema = {
    "@context": "https://schema.org",
    "@type": "Course",
    name: page.keyword,
    description: page.description,
    provider: { "@type": "Organization", name: "DronAcademy", sameAs: SITE_URL },
    offers: {
      "@type": "Offer",
      price: "89.00",
      priceCurrency: "USD",
      url: `${SITE_URL}/register`,
    },
    hasCourseInstance: {
      "@type": "CourseInstance",
      courseMode: "online",
      courseWorkload: "PT6H",
    },
  };
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: page.faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL },
      { "@type": "ListItem", position: 2, name: page.keyword, item: `${SITE_URL}${path}` },
    ],
  };

  return (
    <div className="min-h-screen">
      <Seo title={page.title} description={page.description} path={path} image={page.image}
        jsonLd={[courseSchema, faqSchema, breadcrumbSchema]} />

      {/* Header */}
      <header className="glass border-b border-border/60 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="seo-logo">
            <div className="h-9 w-9 rounded-sm bg-primary flex items-center justify-center glow-cyan">
              <Plane className="h-5 w-5 text-primary-foreground" strokeWidth={2.5} />
            </div>
            <span className="font-display font-black text-lg">DRON<span className="text-primary">ACADEMY</span></span>
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/login"><Button variant="ghost" data-testid="seo-login">Log in</Button></Link>
            <Link to="/register"><Button className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm" data-testid="seo-signup">Get Started</Button></Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={page.image} alt={page.keyword} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-background/85" />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/50 to-transparent" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 pt-20 pb-24 md:pt-28 md:pb-32">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="max-w-2xl">
            <span className="inline-block text-xs font-mono uppercase tracking-[0.3em] text-primary mb-5">{page.overline}</span>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.05] mb-6">{page.h1}</h1>
            <p className="text-base md:text-lg text-slate-300 mb-8 max-w-xl leading-relaxed">{page.subhead}</p>
            <div className="flex flex-wrap gap-4">
              <Link to="/register">
                <Button size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm h-12 px-7 text-base font-semibold group" data-testid="seo-cta-primary">
                  Start Learning Free <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                </Button>
              </Link>
            </div>
            <div className="flex items-center gap-2 mt-8 text-sm text-muted-foreground">
              <Star className="h-4 w-4 text-accent fill-accent" />
              <span>Trusted by pilots preparing for FAA Part 107 certification</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Benefits */}
      <section className="max-w-7xl mx-auto px-6 py-16 md:py-20 grid lg:grid-cols-2 gap-12 items-start">
        <div>
          <span className="text-xs font-mono uppercase tracking-[0.3em] text-accent">Why DronAcademy</span>
          <h2 className="font-display text-3xl md:text-4xl font-bold mt-3 mb-6">Built to get you flying — fast</h2>
          <ul className="space-y-4">
            {page.benefits.map((b) => (
              <li key={b} className="flex items-start gap-3 text-slate-300">
                <Check className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                <span className="text-[15px]">{b}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          {page.learn.map((l, i) => (
            <motion.div key={l.t} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.06 }}
              className="p-5 rounded-sm border border-border/70 bg-card">
              <GraduationCap className="h-5 w-5 text-primary mb-3" />
              <h3 className="font-display font-bold mb-1.5">{l.t}</h3>
              <p className="text-sm text-slate-400 leading-relaxed">{l.d}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Lead capture */}
      <section className="max-w-3xl mx-auto px-6 pb-4">
        <LeadCaptureForm
          source={`seo-${slug}`}
          heading="Not ready to sign up yet?"
          subheading="Grab a free FAA Part 107 study checklist — instant access, no commitment."
        />
      </section>

      {/* FAQ */}
      <section className="max-w-3xl mx-auto px-6 py-16 md:py-20">
        <span className="text-xs font-mono uppercase tracking-[0.3em] text-accent">FAQ</span>
        <h2 className="font-display text-3xl md:text-4xl font-bold mt-3 mb-8">Frequently asked questions</h2>
        <div className="space-y-4">
          {page.faqs.map((f) => (
            <div key={f.q} className="p-5 rounded-sm border border-border/70 bg-card">
              <h3 className="font-display font-bold mb-2">{f.q}</h3>
              <p className="text-sm text-slate-400 leading-relaxed">{f.a}</p>
            </div>
          ))}
        </div>
        <div className="mt-10 p-8 rounded-sm border border-primary/40 bg-primary/5 text-center">
          <h2 className="font-display text-2xl font-bold mb-2">Ready to take off?</h2>
          <p className="text-slate-400 mb-6">Start free, then unlock the full course whenever you're ready.</p>
          <Link to="/register">
            <Button size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm h-12 px-8 font-semibold" data-testid="seo-cta-footer">
              Create your free account
            </Button>
          </Link>
        </div>
      </section>

      <footer className="border-t border-border/60 py-8">
        <div className="max-w-7xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-muted-foreground">
          <span className="font-mono">DRONACADEMY © 2026</span>
          <div className="flex flex-wrap gap-4">
            <Link to="/learn/faa-part-107-course" className="hover:text-primary">FAA Part 107</Link>
            <Link to="/learn/how-to-fly-a-drone" className="hover:text-primary">How to Fly</Link>
            <Link to="/learn/drone-training-for-beginners" className="hover:text-primary">For Beginners</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
